import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '~/app/auth/context/AuthContext';
import {
  getSiteAssetStatus,
  type SiteAssetStatus,
} from '~/app/hooks/site/useSiteAssetMap';
import {
  SiteService,
  type SiteAssetMapAsset,
  type SiteLookup,
  type ZoneLookup,
} from '~/app/services/site/site.service';

export function useZoneAssetMap(siteId: string, zoneId: string) {
  const { user } = useAuth();
  const organizationId = user?.organizationId ?? '';

  const query = useQuery({
    queryKey: ['zones', 'assets-map', organizationId, siteId, zoneId],
    queryFn: () =>
      SiteService.findZoneAssetsMapById(organizationId, siteId, zoneId),
    enabled:
      Boolean(organizationId) &&
      Boolean(siteId.trim()) &&
      Boolean(zoneId.trim()),
  });

  const site = query.data?.site ?? null;
  const zone = query.data?.zone ?? null;
  const assets = useMemo(() => query.data?.assets ?? [], [query.data?.assets]);

  const statusCounts = useMemo(() => {
    return assets.reduce(
      (accumulator, asset) => {
        const status = getSiteAssetStatus(asset.openIncidentsCount);
        accumulator[status] += 1;
        return accumulator;
      },
      {
        healthy: 0,
        warning: 0,
        critical: 0,
      } satisfies Record<SiteAssetStatus, number>,
    );
  }, [assets]);

  return {
    assets,
    site,
    zone,
    statusCounts,
    error: query.error ?? null,
    isFetching: query.isFetching,
    isLoading: query.isLoading,
    refetch: query.refetch,
  } satisfies {
    assets: SiteAssetMapAsset[];
    site: SiteLookup | null;
    zone: ZoneLookup | null;
    statusCounts: Record<SiteAssetStatus, number>;
    error: Error | null;
    isFetching: boolean;
    isLoading: boolean;
    refetch: () => Promise<unknown>;
  };
}
